import { Config } from '@/types/config';
import { Answer } from '@/types/answer';
import { storePrompts } from '@/utils/plugins/prompts';

// Capture the landing URL at module load — bundle init runs before any
// step transition fires history.replaceState (flow.ts:264 rewrites the
// URL on every step, destroying the landing URL). The captured value is
// sent up to the Tornado BFF as `sourceUrl` on the initial flow-start
// request, and the runner-bff parses Google attribution params out of it.
// Frozen at module scope so subsequent InitialRequest constructions (re-
// init scenarios, multiple widget instances on the page) all see the
// same landing URL, not whatever step-specific URL is current. Optional
// chaining off globalThis.window keeps it safe in non-browser contexts
// (SSR/tests): globalThis.window is undefined there, so this is undefined.
//
// Deliberately NOT scrubbed of credential params (ab#281964 review):
// runner-ui strips CREDENTIAL_PARAMS ('h', 'token') before freezing its
// copy because its /launch handoff puts a live one-shot credential in a
// navigable URL. The widget has no counterpart: no /launch-style route,
// and no credential ever rides a URL the browser navigates to. The one
// credential-in-query in this repo (stateId on /insurance-card-image)
// travels on an img/XHR sub-resource request, which can never be the
// host page's window.location.href captured here. The only URL params
// the widget itself consumes are the odr-* init params (deep-link flow
// config, not secrets — see utils/parse-get-params.ts). If a
// credential-bearing param ever appears on a navigable widget or host-
// page URL, scrub it here BEFORE this capture, copying runner-ui's
// CREDENTIAL_PARAMS pattern (workflow-runner-ui/src/api/workflowApi.ts):
// since ab#281964 this URL is forwarded and INFO-logged on every session
// start, so a leaked credential would land in every session's log and
// audit record, not one in a hundred.
const LANDING_URL: string | undefined = globalThis.window?.location?.href;

export enum Actions {
    ANSWER = 'answer',
    CANCEL = 'cancel',
    CONFIRM = 'confirm',
    START = 'start',
    JUMP = 'jump',
    MODIFY = 'modify'
}

export class AppointRequest {
    formAction: string | null;
    formMethod: string | null;
    language: string;
    prompts: Array<string>;
    action = Actions.ANSWER;
    stateId: string;
    stub?: string;
    answers: Array<Answer>;
    clinicId: string | number | null;
    ivrServer?: string;
    form = false;
    stepXhrTimeout: number | null;
    constructor (props: Partial<AppointRequest> & Pick<AppointRequest, 'answers' | 'stateId'>, appConfig: Config) {
        let {
            formAction,
            formMethod,
            language,
            action,
            stateId,
            form,
            answers,
            stepXhrTimeout,
        } = props;

        this.formAction = formAction || null;
        this.formMethod = formMethod || null;
        this.language = language || appConfig.language;
        this.prompts = Object.keys(storePrompts);
        this.action = action || Actions.ANSWER;
        this.stateId = stateId;
        this.form = form || false;
        this.answers = answers;
        this.stepXhrTimeout = stepXhrTimeout === undefined ? null : stepXhrTimeout;
        this.stub = appConfig.stub instanceof Array ? appConfig.stub.shift() : appConfig.stub;
        this.clinicId = appConfig.clinicId;

        if (window.__odrDebugIvrServer) {
            this.ivrServer = window.__odrDebugIvrServer;
        }
    }
};

export class InitialRequest {
    language: string;
    chatMode: boolean;
    clinicId: string | number | null;
    prompts: Array<string>;
    initParams: object;
    stub?: string;
    ivrServer?: string;
    // Landing-page URL forwarded to the Tornado BFF for Google Reserve
    // attribution. The runner-bff parses Google attribution params out of
    // this URL server-side; appoint-widget doesn't filter or whitelist.
    // Top-level (not in initParams) so it doesn't bleed into serviceivr
    // CallState — DESIGN_google-booking.md forbids that. See
    // _docs/PLAN_support-google-booking.md.
    sourceUrl?: string;

    constructor (props: Partial<InitialRequest>, appConfig: Config) {
        this.language = props.language === undefined ? appConfig.language : props.language;
        this.chatMode = props.chatMode === undefined ? appConfig.mode === 'chat' : props.chatMode;
        this.clinicId = props.clinicId === undefined ? appConfig.clinicId : props.clinicId;
        this.prompts = Object.keys(storePrompts);
        this.initParams = {
            Language: appConfig.language,
            ...appConfig.initParams,
            ...(props.initParams || {}),
            ...(window.__odrDebugEmail ? { _debugEmail: window.__odrDebugEmail } : {}),
        };
        this.stub = appConfig.stub instanceof Array ? appConfig.stub.shift() : appConfig.stub;
        if (window.__odrDebugIvrServer) {
            this.ivrServer = window.__odrDebugIvrServer;
        }
        // sourceUrl is the module-scoped LANDING_URL frozen at bundle init —
        // step transitions rewrite window.location.href via replaceState,
        // so reading it here at construction time on a later request would
        // give a step-specific URL rather than the actual landing URL.
        // props.sourceUrl wins if explicitly provided (test overrides).
        this.sourceUrl = props.sourceUrl || LANDING_URL;
    }
};

export interface MixedRequest extends AppointRequest, InitialRequest {}
